import { useQuery } from "@tanstack/react-query";
import { Alert, Button, Empty, Skeleton, Timeline, Typography } from "antd";
import { useState } from "react";
import { api, unwrap } from "@/api/client";
import { useErrorText } from "@/api/errors";
import { useI18n } from "@/i18n";

/** Marketplaces whose courier history Zerpid can read (TikTok Shop runs Tokopedia too). */
export const trackable = (platform: string) => platform === "tiktok" || platform === "tokopedia";

/**
 * The courier's history, read from the marketplace only when asked: every
 * read spends the store's API quota, so it is not loaded with the order.
 */
export function TrackingHistory({ orderId, tz }: { orderId: string; tz?: string }) {
  const { t, formatDateTime } = useI18n();
  const errorText = useErrorText();
  const [open, setOpen] = useState(false);
  const q = useQuery({
    queryKey: ["orders", "tracking", orderId],
    queryFn: () => unwrap(api.GET("/v1/orders/{orderID}/tracking", { params: { path: { orderID: orderId } } })),
    enabled: open,
    staleTime: 5 * 60_000,
    retry: false,
  });

  if (!open) {
    return (
      <Button size="small" onClick={() => setOpen(true)}>
        {t("orders.trackParcel")}
      </Button>
    );
  }
  if (q.isPending) return <Skeleton active paragraph={{ rows: 2 }} title={false} />;
  if (q.isError) return <Alert type="warning" showIcon title={errorText(q.error)} />;
  if (q.data.events.length === 0) return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("orders.trackingEmpty")} />;
  return (
    <Timeline
      items={[...q.data.events].reverse().map((e, i) => ({
        color: i === 0 ? "blue" : "gray",
        content: (
          <>
            <Typography.Text>{e.description}</Typography.Text>
            <br />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {formatDateTime(e.time, tz)}
            </Typography.Text>
          </>
        ),
      }))}
    />
  );
}
